import React, { useState } from "react";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  FormLabel,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import { COLORS, fontFamilyAll } from "../assets/commonComponents/COLORS";
import { googleMapLink } from "../assets/commonComponents/data";
import ToastMessages from "../assets/commonComponents/ToastMessages";
import { submitFormAPI } from "../Util/UtilApi";

// Field styles (unchanged)
const FieldStyles = (error) => ({
  fontFamily: `${fontFamilyAll} !important`,
  "& .MuiOutlinedInput-root": {
    fontFamily: `${fontFamilyAll} !important`,
    borderRadius: "12px",
    backgroundColor: "#fafafa",
    "& fieldset": {
      borderColor: error ? "red !important" : `${COLORS.secondaryColor} !important`,
    },
    "&:hover fieldset": {
      borderColor: error ? "red !important" : `${COLORS.secondaryColor} !important`,
    },
    "&.Mui-focused fieldset": {
      borderColor: error ? "red !important" : `${COLORS.secondaryColor} !important`,
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: `${fontFamilyAll} !important`,
    color: error ? "red !important" : `${COLORS.secondaryColor} !important`,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: error ? "red !important" : `${COLORS.secondaryColor} !important`,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: `${fontFamilyAll} !important`,
    color: error ? "red !important" : `${COLORS.secondaryColor} !important`,
  },
});

const titleOptions = ["Mr.", "Miss.", "Mrs.", "Dr."];
const communicationOptions = ["Email", "Call", "WhatsApp", "SMS"];

const ContactForm = ({ headingText, subHeadingText, buttonText }) => {
  const initialValues = {
    title: "Mr.",
    firstName: "",
    lastName: "",
    mobile: "",
    altMobile: "",
    email: "",
    company: "",
    communication: [],
    consent: false,
  };

  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ---------- Validation ----------
  const validateField = (name, value) => {
    if (name === "altMobile") {
      return value && !/^\d{10}$/.test(value) ? "Enter valid 10-digit number" : "";
    }

    switch (name) {
      case "title":
        if (!value) return "Title is required";
        break;
      case "firstName":
      case "lastName":
      case "mobile":
      case "email":
      case "company":
        if (!value?.toString().trim())
          return `${name === "firstName" ? "First" : name === "lastName" ? "Last" : name === "mobile" ? "Mobile" : name === "company" ? "Company" : "Email"} is required`;
        break;
      case "mobile":
        if (!/^\d{10}$/.test(value)) return "Enter a valid 10-digit mobile number";
        break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Enter a valid email address";
        break;
      case "communication":
        if (!Array.isArray(value) || value.length === 0) return "Select at least one communication method";
        break;
      case "consent":
        if (!value) return "You must provide consent";
        break;
      default:
        break;
    }
    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formValues).forEach((key) => {
      const error = validateField(key, formValues[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newValue;
    if (name === "communication") {
      const current = formValues.communication || [];
      newValue = checked ? [...current, value] : current.filter((v) => v !== value);
    } else if (type === "checkbox") {
      newValue = checked;
    } else {
      newValue = value;
    }

    setFormValues((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, newValue) }));
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const payload = {
        data: {
          title: formValues.title,
          firstname: formValues.firstName,
          lastname: formValues.lastName,
          email: formValues.email,
          mobile: formValues.mobile,
          phone: formValues.altMobile || null,
          companyname: formValues.company,
          preferredcommunicationmethod: formValues.communication.join(";"),
          consentprovided: formValues.consent,
          origin: "Website",
        },
      };

      // await submitFormAPI(payload);

      setSnackbarMessage("Thank you! Your form has been submitted successfully.");
      setSnackbarOpen(true);

      setFormValues(initialValues);
      setErrors({});
    } catch (error) {
      console.error("Form submission error:", error);
      setSnackbarMessage("Failed to submit. Please try again later.");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box maxWidth="800px" mx="auto" py={6}>
      <ToastMessages
        isOpen={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={snackbarMessage}
        severity={snackbarMessage.includes("Thank you") ? true : false}
      />

      {/* Heading */}
      <Box textAlign="center" mb={5}>
        <Typography
          variant="h4"
          fontWeight="bold"
          color={COLORS.secondaryColor}
          gutterBottom
          fontFamily={fontFamilyAll}
        >
          {headingText}
        </Typography>
        <Typography variant="subtitle1" color={COLORS.primaryColor} fontFamily={fontFamilyAll}>
          {subHeadingText}
        </Typography>
      </Box>

      {/* Form Card */}
      <Card
        sx={{
          p: 4,
          mb: 5,
          borderRadius: "16px",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          border: `1px solid ${COLORS.secondaryColor}30`,
        }}
      >
        <CardContent>
          <form onSubmit={handleSubmit}>
            {/* Title + Names */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <FormControl sx={{ flex: 1, minWidth: 120 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.title ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  Title *
                </FormLabel>
                <TextField
                  select
                  name="title"
                  value={formValues.title}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.title)}
                  error={!!errors.title}
                  helperText={errors.title}
                >
                  {titleOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <FormControl sx={{ flex: 2, minWidth: 200 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.firstName ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  First Name *
                </FormLabel>
                <TextField
                  name="firstName"
                  placeholder="John"
                  value={formValues.firstName}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.firstName)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                />
              </FormControl>

              <FormControl sx={{ flex: 2, minWidth: 200 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.lastName ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  Last Name *
                </FormLabel>
                <TextField
                  name="lastName"
                  placeholder="Doe"
                  value={formValues.lastName}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.lastName)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                />
              </FormControl>
            </Box>

            {/* Mobile Numbers */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <FormControl sx={{ flex: 1 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.mobile ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  Mobile *
                </FormLabel>
                <TextField
                  name="mobile"
                  type="tel"
                  placeholder="9876543210"
                  value={formValues.mobile}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.mobile)}
                  error={!!errors.mobile}
                  helperText={errors.mobile || "10 digits required"}
                  inputProps={{ maxLength: 10 }}
                />
              </FormControl>

              <FormControl sx={{ flex: 1 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.altMobile ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  Alternate Mobile
                </FormLabel>
                <TextField
                  name="altMobile"
                  type="tel"
                  placeholder="9876543210 (optional)"
                  value={formValues.altMobile}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.altMobile)}
                  error={!!errors.altMobile}
                  helperText={errors.altMobile}
                  inputProps={{ maxLength: 10 }}
                />
              </FormControl>
            </Box>

            {/* Email & Company */}
            <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
              <FormControl sx={{ flex: 2 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.email ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  Email *
                </FormLabel>
                <TextField
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formValues.email}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.email)}
                  error={!!errors.email}
                  helperText={errors.email}
                />
              </FormControl>

              <FormControl sx={{ flex: 2 }}>
                <FormLabel sx={{ mb: 1, fontWeight: 500, color: errors.company ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}>
                  Company Name*
                </FormLabel>
                <TextField
                  name="company"
                  placeholder="ABC Corporation"
                  value={formValues.company}
                  onChange={handleChange}
                  sx={FieldStyles(!!errors.company)}
                  error={!!errors.company}
                  helperText={errors.company}
                />
              </FormControl>
            </Box>

            {/* Communication */}
            <FormControl component="fieldset" error={!!errors.communication} margin="normal">
              <FormLabel
                component="legend"
                sx={{ mb: 1, fontWeight: 500, color: errors.communication ? "red" : COLORS.secondaryColor, fontFamily: fontFamilyAll }}
              >
                Preferred Communication Method *
              </FormLabel>
              <FormGroup row>
                {communicationOptions.map((opt) => (
                  <FormControlLabel
                    key={opt}
                    control={
                      <Checkbox
                        checked={formValues.communication.includes(opt)}
                        onChange={handleChange}
                        name="communication"
                        value={opt}
                        sx={{ color: COLORS.secondaryColor, "&.Mui-checked": { color: COLORS.primaryColor } }}
                      />
                    }
                    label={<Typography fontFamily={fontFamilyAll}>{opt}</Typography>}
                  />
                ))}
              </FormGroup>
              {errors.communication && (
                <FormHelperText sx={{ color: "red", fontFamily: fontFamilyAll }}>
                  {errors.communication}
                </FormHelperText>
              )}
            </FormControl>

            {/* ---------- CONSENT (fixed alignment) ---------- */}
            <Box mt={3} mb={2}>
              <FormControl error={!!errors.consent}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formValues.consent}
                      onChange={handleChange}
                      name="consent"
                      sx={{ color: COLORS.secondaryColor, "&.Mui-checked": { color: COLORS.primaryColor } }}
                    />
                  }
                  label={
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: fontFamilyAll,
                        color: errors.consent ? "red" : COLORS.secondaryColor,
                        fontWeight: 500,
                      }}
                    >
                      I consent to receive communications and agree to the terms *
                    </Typography>
                  }
                />
                {errors.consent && (
                  <FormHelperText sx={{ color: "red", fontFamily: fontFamilyAll }}>
                    {errors.consent}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Submit */}
            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading || !formValues.consent}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 600,
                  borderRadius: "30px",
                  textTransform: "none",
                  fontFamily: fontFamilyAll,
                  backgroundColor: COLORS.primaryColor,
                  "&:hover": { backgroundColor: COLORS.primaryColor },
                  minWidth: 160,
                }}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                    Submitting...
                  </>
                ) : (
                  buttonText || "Submit"
                )}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Google Map */}
      <Box>
        <iframe
          title="Our Location"
          width="100%"
          height="400"
          style={{ border: 0, borderRadius: "16px" }}
          src={googleMapLink}
          allowFullScreen
          loading="lazy"
        ></iframe>
      </Box>
    </Box>
  );
};

export default ContactForm;